import { applyPatch, compare } from "fast-json-patch";

import type { AppSchema } from "@shared/types";

export type JsonPatchOperation = {
  op: "add" | "remove" | "replace" | "move" | "copy" | "test";
  path: string;
  value?: unknown;
  from?: string;
};

/**
 * Compute the JSON Patch (RFC 6902) that transforms `prev` into `next`.
 */
export function diffSchema(prev: AppSchema, next: AppSchema): JsonPatchOperation[] {
  return compare(prev, next) as JsonPatchOperation[];
}

/**
 * Apply a JSON Patch to a schema and return the new document.
 * Returns `null` if the patch cannot be applied.
 */
export function applyPatchToSchema(
  schema: AppSchema,
  patch: JsonPatchOperation[],
): AppSchema | null {
  try {
    const result = applyPatch(schema, patch as never, false, false);
    return result.newDocument as AppSchema;
  } catch {
    return null;
  }
}

/**
 * Compute the inverse patch that undoes `patch`.
 */
export function invertPatch(patch: JsonPatchOperation[]): JsonPatchOperation[] {
  const inverse: JsonPatchOperation[] = [];
  for (let i = patch.length - 1; i >= 0; i--) {
    const op = patch[i];
    if (!op) continue;
    switch (op.op) {
      case "add":
        inverse.push({ op: "remove", path: op.path });
        break;
      case "remove":
        // We don't know the removed value here; callers should generate
        // inverse patches from a full diff instead.
        inverse.push({ op: "add", path: op.path, value: undefined });
        break;
      case "replace":
        inverse.push({ op: "replace", path: op.path, value: undefined });
        break;
      case "move":
        inverse.push({ op: "move", path: op.from ?? op.path, from: op.path });
        break;
      default:
        break;
    }
  }
  return inverse;
}

/**
 * Try to merge a remote patch into the local schema.
 * Returns the merged schema and whether a conflict occurred.
 */
export function tryMergeRemotePatch(
  localSchema: AppSchema,
  remotePatch: JsonPatchOperation[],
): { schema: AppSchema; conflict: boolean } {
  const merged = applyPatchToSchema(localSchema, remotePatch);
  if (merged) {
    return { schema: merged, conflict: false };
  }
  return { schema: localSchema, conflict: true };
}
